import React, { FC, useEffect } from 'react';
import { select } from '~/lib/select';
import { TrainingState, useTrainingStore } from '~/training/state';
import { languageName } from '~/lib/translations/languageName';
import styled, { x } from '@xstyled/styled-components';
import { useLightSwitchesAudioEffect } from '../audio-effect/useLightSwitchesAudioEffect';
import { TickMarkCapsule } from '~/ui/capsules';

interface Props {
  active: true | false;
}

const LocaleItem = styled.divBox<Props>`
  cursor: pointer;
  border-bottom: 1px solid #e0e0e0;
  font-size: 16px;
  line-height: 24px;
  padding-top: 4px;
  padding-bottom: 4px;
  ${p =>
    p.active &&
    `
        font-size: 17px;
        font-weight: 900;
    `}
  .ticker {
    float: right;
  }
`;

const Select = styled.select`
  background-color: white;
  color: black;
`;

export function resolveLocale(locale: string, available: string[]) {
  const language = locale.replace(/-.*$/, '');

  if (available.includes(locale)) {
    // check full culture, example: fr-BE
    return locale;
  } else if (available.includes(language)) {
    // check just language, example: fr
    return language;
  } else {
    return 'en';
  }
}

const selector = (s: TrainingState) =>
  select(s, 'setTextLocale', 'textLocale', 'languageAutoDetect');

const Content = styled.span`
  flex: 1;
`;

export const LocaleSelector: FC<{
  locales: string[];
  reloadSubtitle?: boolean;
  isOnboarding?: boolean;
}> = ({ locales, reloadSubtitle, isOnboarding }) => {
  const { textLocale, setTextLocale, languageAutoDetect } =
    useTrainingStore(selector);
  const [playAudioEffect] = useLightSwitchesAudioEffect();

  const languageItems = locales.map(l => ({
    locale: l,
    label:
      l !== textLocale
        ? `${languageName(l, 'en')} | ${languageName(l)}`
        : `${languageName(l)}`
  }));

  useEffect(() => {
    let resolved = resolveLocale(window.navigator.language, locales);

    if (
      textLocale &&
      window.navigator.language != textLocale &&
      resolved == 'en' &&
      languageAutoDetect
    ) {
      resolved = resolveLocale(textLocale, locales);
    }

    if (locales.includes(resolved) && languageAutoDetect) {
      if (resolved != textLocale) {
        setTextLocale(resolved, isOnboarding);
      }
    } else {
      if (textLocale) {
        setTextLocale(textLocale, isOnboarding);
      } else {
        setTextLocale('en', isOnboarding);
      }
    }
  }, []);

  return locales.length <= 4 ? (
    <x.div>
      {languageItems.map(i => {
        return (
          <LocaleItem
            key={i.locale}
            onClick={() => {
              try {
                playAudioEffect();
              } catch (error) {
                // Silently ignore audio effect errors
              }
              setTextLocale(i.locale, reloadSubtitle || false, isOnboarding);
            }}
            active={i.locale == textLocale}
          >
            <Content>{i.label}</Content>
            {i.locale == textLocale && (
              <span className="ticker">
                <TickMarkCapsule />
              </span>
            )}
          </LocaleItem>
        );
      })}
    </x.div>
  ) : (
    <Select
      onChange={e => {
        playAudioEffect();
        setTextLocale(e.target.value, reloadSubtitle || false, isOnboarding);
      }}
      value={textLocale}
    >
      {locales.map(l => (
        <option key={l} value={l}>
          {languageName(l)}
        </option>
      ))}
    </Select>
  );
};
